/**
 * FullPageDetailTemplate — Full-width detail/editor page
 *
 * Matches OnMove-Admin RoleDetailEditor layout:
 *  1. DashboardNavbar at top
 *  2. Header Card (gradient background)
 *     - Back button + Title + Chips/badges + Action buttons
 *     - Stats row
 *  3. Optional warning/info Alert bar
 *  4. Optional search/filter bar
 *  5. Collapsible sections (children)
 *  6. Snackbar for notifications
 *
 * Props:
 *   title           — main heading text
 *   subtitle        — description below title
 *   titleIcon       — optional icon element next to title
 *   chips           — [{ label, color, bgcolor }] badges after title
 *   stats           — [{ label, value }] stat counters in header
 *   headerColor     — gradient color key (primary, info, success, etc.)
 *   backRoute       — URL to navigate back (shows back arrow)
 *   loading         — show spinner
 *   notFound        — show not-found alert
 *   notFoundMessage — text for not-found
 *   permissionDenied — show permission-denied alert
 *   alerts          — [{ severity, content }] alert bars below header
 *   searchPlaceholder — if set, shows search bar; controlled via searchQuery/onSearchChange
 *   searchQuery     — controlled search value
 *   onSearchChange  — (value) => void
 *   actions         — React node for header action buttons (save, delete, etc.)
 *   children        — collapsible sections (use <CollapsibleSection>)
 *   snackbar        — { open, severity, title, message }
 *   onSnackbarClose — () => void
 */

import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Card,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useTheme } from "@mui/material/styles";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDSnackbar from "components/KDSnackbar";
import DashboardNavbar from "components/DashboardNavbar";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";

function FullPageDetailTemplate({
  title,
  subtitle,
  titleIcon,
  chips,
  stats,
  headerColor,
  backRoute,
  breadcrumbLabel,
  loading,
  notFound,
  notFoundMessage,
  permissionDenied,
  alerts,
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  actions,
  children,
  snackbar,
  onSnackbarClose,
}) {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const { palette } = useTheme();
  const t = useT();

  const layoutSx = ({ breakpoints, transitions }) => ({
    p: 3,
    marginLeft: miniSidenav ? "80px" : "260px",
    transition: transitions.create("margin-left", {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.standard,
    }),
    [breakpoints.down("xl")]: { marginLeft: 0 },
  });

  // Permission denied
  if (permissionDenied) {
    return (
      <KDBox sx={layoutSx}>
        <DashboardNavbar breadcrumbLabel={breadcrumbLabel} />
        <KDBox mt={5}>
          <Alert severity="error">{t("error.forbidden")}</Alert>
        </KDBox>
      </KDBox>
    );
  }

  // Loading
  if (loading) {
    return (
      <KDBox sx={layoutSx}>
        <DashboardNavbar breadcrumbLabel={breadcrumbLabel} />
        <KDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress sx={{ color: "primary.main" }} />
        </KDBox>
      </KDBox>
    );
  }

  // Not found
  if (notFound) {
    return (
      <KDBox sx={layoutSx}>
        <DashboardNavbar breadcrumbLabel={breadcrumbLabel} />
        <KDBox mt={5}>
          <Alert severity="error">{notFoundMessage || t("error.notFound")}</Alert>
        </KDBox>
      </KDBox>
    );
  }

  return (
    <KDBox sx={layoutSx}>
      <DashboardNavbar breadcrumbLabel={breadcrumbLabel} />

      {/* ── Header Card ── */}
      <Card
        sx={({ palette }) => {
          const grad = palette.gradients?.[headerColor] || palette.gradients?.primary;
          const mainColor = palette[headerColor]?.main || palette.primary.main;
          return {
            mt: 5,
            p: 3,
            background: darkMode
              ? `linear-gradient(135deg, ${mainColor}22 0%, ${mainColor}11 100%)`
              : `linear-gradient(135deg, ${grad?.state || mainColor} 0%, ${grad?.main || mainColor} 100%)`,
            backdropFilter: darkMode ? "blur(10px)" : "none",
            border: darkMode ? `1px solid ${mainColor}33` : "none",
            borderRadius: "16px",
            boxShadow: darkMode ? "none" : `0 8px 32px ${mainColor}30`,
            color: "white",
          };
        }}
      >
        <KDBox display="flex" justifyContent="space-between" alignItems="flex-start">
          {/* Left: Back + Title */}
          <KDBox display="flex" alignItems="center" gap={2}>
            {backRoute && (
              <Tooltip title={t("common.back")}>
                <IconButton
                  onClick={() => navigate(backRoute)}
                  sx={{ color: "white" }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            )}

            <KDBox>
              <KDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {titleIcon}
                <KDTypography variant="h4" color="white" fontWeight="bold">
                  {title}
                </KDTypography>
                {chips?.map((chip, idx) => (
                  <KDBox
                    key={idx}
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.3,
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      bgcolor: chip.bgcolor || palette.headerOverlay.chip,
                      color: chip.color || "white",
                      ml: 0.5,
                    }}
                  >
                    {chip.label}
                  </KDBox>
                ))}
              </KDBox>
              {subtitle && (
                <KDTypography variant="body2" color="white" sx={{ mt: 0.5, opacity: 0.8 }}>
                  {subtitle}
                </KDTypography>
              )}
            </KDBox>
          </KDBox>

          {/* Right: Actions */}
          {actions && (
            <KDBox display="flex" gap={1} alignItems="center">
              {actions}
            </KDBox>
          )}
        </KDBox>

        {/* Stats Row */}
        {stats && stats.length > 0 && (
          <KDBox display="flex" gap={4} mt={3} flexWrap="wrap">
            {stats.map((stat, idx) => (
              <KDBox key={idx}>
                <KDTypography variant="caption" color="white" sx={{ opacity: 0.7 }}>
                  {stat.label}
                </KDTypography>
                <KDTypography variant="h5" color="white" fontWeight="bold">
                  {stat.value}
                </KDTypography>
              </KDBox>
            ))}
          </KDBox>
        )}
      </Card>

      {/* ── Alerts ── */}
      {alerts?.map((alert, idx) => (
        <Alert key={idx} severity={alert.severity} sx={{ mt: 2, borderRadius: "12px" }}>
          {alert.content}
        </Alert>
      ))}

      {/* ── Search/Filter Bar ── */}
      {searchPlaceholder && onSearchChange && (
        <Card
          sx={{
            mt: 2,
            p: 2,
            background: palette.background.glass,
            backdropFilter: "blur(10px)",
            border: `1px solid ${palette.background.glassBorder}`,
            borderRadius: "16px",
          }}
        >
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchQuery || ""}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: palette.text.disabled }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange("")}
                    sx={{ color: palette.text.disabled }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: palette.background.cardInner,
                borderRadius: "12px",
                color: palette.text.main,
                "& fieldset": {
                  borderColor: palette.inputBorderColor,
                },
                "&:hover fieldset": {
                  borderColor: palette.inputBorderHoverColor,
                },
                "&.Mui-focused fieldset": { borderWidth: "2px" },
              },
              "& .MuiInputBase-input::placeholder": {
                color: palette.text.disabled,
                opacity: 1,
              },
            }}
          />
        </Card>
      )}

      {/* ── Sections (children) ── */}
      <KDBox mt={2}>
        {children}
      </KDBox>

      {/* ── Snackbar ── */}
      {snackbar && (
        <KDSnackbar
          open={snackbar.open}
          onClose={onSnackbarClose}
          severity={snackbar.severity || "info"}
          title={snackbar.title}
          message={snackbar.message}
        />
      )}
    </KDBox>
  );
}

FullPageDetailTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  titleIcon: PropTypes.node,
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      bgcolor: PropTypes.string,
    })
  ),
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  headerColor: PropTypes.string,
  backRoute: PropTypes.string,
  loading: PropTypes.bool,
  notFound: PropTypes.bool,
  notFoundMessage: PropTypes.string,
  permissionDenied: PropTypes.bool,
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      severity: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    })
  ),
  searchPlaceholder: PropTypes.string,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func,
  actions: PropTypes.node,
  children: PropTypes.node,
  snackbar: PropTypes.shape({
    open: PropTypes.bool,
    severity: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
  }),
  onSnackbarClose: PropTypes.func,
};

FullPageDetailTemplate.defaultProps = {
  subtitle: null,
  titleIcon: null,
  chips: null,
  stats: null,
  headerColor: "primary",
  backRoute: null,
  loading: false,
  notFound: false,
  notFoundMessage: null,
  permissionDenied: false,
  alerts: null,
  searchPlaceholder: null,
  searchQuery: "",
  onSearchChange: null,
  actions: null,
  children: null,
  snackbar: null,
  onSnackbarClose: null,
};

export default FullPageDetailTemplate;
